/* src/components/RichTextEditor.js - v1.2 - Links Default to New Tab
   CHANGELOG (v1.1 -> v1.2): Links inserted here (the Welcome-modal editor)
   opened in the same window/tab, unlike post text - Post.js's urlRegex
   linkify wraps every URL in target="_blank" at render time, but this
   editor's Link button uses document.execCommand('createLink', ...),
   which has no way to set target/rel on the anchor it creates - it's a
   bare <a href>. Fixed in handleInput (runs after every edit, not just
   the Link button, so it also normalizes any link pasted in directly):
   any <a> in the editor missing target="_blank" gets it added, plus
   rel="noopener noreferrer" for the same security reason Post.js's
   linkified links already carry that pairing.
   CHANGELOG (v1.0 -> v1.1): Added Left/Center/Right alignment buttons
   CHANGELOG (v1.0 -> v1.1): Added Left/Center/Right alignment buttons
   (execCommand justifyLeft/justifyCenter/justifyRight). These set an
   explicit inline text-align on the block the caret is in, which is
   enough to override the welcome modal's inherited center-alignment
   (App.js's .card wrapper has textAlign:'center') for whichever specific
   paragraph the admin aligns - no App.css change needed for this to work.
   CHANGELOG: v1.0 - Initial Build
   New component: a small WYSIWYG-style editor (toolbar + contentEditable)
   for admin-authored HTML content - first consumer is AdminDashboard.js's
   new "What's New Modal" tab (welcome_message_html/whats_new_html, see
   app.py v13.7 / routes/admin.py v3.14). Uses document.execCommand for
   formatting - deprecated by spec but still universally supported in
   Chrome/Firefox/Safari for exactly this kind of basic admin-only
   editor, and avoids pulling in a new heavyweight dependency (this
   project already hit real peer-dependency conflicts with React 19 once,
   see the caniuse-lite/npm-audit work - a full rich-text-editor package
   would be a much bigger version of that same risk).
   Uncontrolled-DOM pattern: the contentEditable div's innerHTML is only
   synced from the `value` prop on mount and when `value` changes from
   OUTSIDE (e.g. switching tabs and back, or the initial fetch landing) -
   never on every keystroke. Re-setting innerHTML on every render would
   reset the cursor position to the start on every character typed, a
   well-known contentEditable+React footgun. Typing itself only updates
   local DOM state and fires onChange with the current innerHTML; React
   never re-renders the div's contents while the user is actively editing. */
import React, { useRef, useEffect } from 'react';

function RichTextEditor({ value, onChange, minHeight = '150px' }) {
    const editorRef = useRef(null);
    const lastValueRef = useRef(value);

    useEffect(() => {
        if (editorRef.current && value !== lastValueRef.current && document.activeElement !== editorRef.current) {
            editorRef.current.innerHTML = value || '';
            lastValueRef.current = value;
        }
    }, [value]);

    const handleInput = () => {
        editorRef.current.querySelectorAll('a:not([target])').forEach((a) => {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
        });
        const html = editorRef.current.innerHTML;
        lastValueRef.current = html;
        onChange(html);
    };

    const exec = (command, arg = null) => {
        editorRef.current.focus();
        document.execCommand(command, false, arg);
        handleInput();
    };

    const handleLink = () => {
        const selection = window.getSelection();
        if (!selection || selection.toString().length === 0) {
            alert('Select some text first, then click Link.');
            return;
        }
        const url = window.prompt('Enter a URL:', 'https://');
        if (url) exec('createLink', url);
    };

    const btnStyle = {
        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)',
        color: 'white', padding: '6px 12px', borderRadius: '5px', cursor: 'pointer',
        fontSize: '0.85rem', marginRight: '6px'
    };

    return (
        <div>
            <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap' }}>
                <button type="button" style={{ ...btnStyle, fontWeight: 'bold' }} onClick={() => exec('bold')} title="Bold">B</button>
                <button type="button" style={{ ...btnStyle, fontStyle: 'italic' }} onClick={() => exec('italic')} title="Italic">I</button>
                <button type="button" style={{ ...btnStyle, textDecoration: 'underline' }} onClick={() => exec('underline')} title="Underline">U</button>
                <button type="button" style={btnStyle} onClick={() => exec('insertUnorderedList')} title="Bullet List">• List</button>
                <button type="button" style={btnStyle} onClick={() => exec('justifyLeft')} title="Align Left">⬅</button>
                <button type="button" style={btnStyle} onClick={() => exec('justifyCenter')} title="Align Center">↔</button>
                <button type="button" style={btnStyle} onClick={() => exec('justifyRight')} title="Align Right">➡</button>
                <button type="button" style={btnStyle} onClick={handleLink} title="Insert Link">🔗 Link</button>
                <button type="button" style={btnStyle} onClick={() => exec('unlink')} title="Remove Link">🔗✕</button>
                <button type="button" style={btnStyle} onClick={() => exec('removeFormat')} title="Clear Formatting">Clear</button>
            </div>
            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                style={{
                    minHeight, padding: '12px', borderRadius: '5px',
                    background: 'rgba(0,0,0,0.3)', color: 'white',
                    lineHeight: '1.5', outline: 'none', border: '1px solid rgba(255,255,255,0.1)'
                }}
            />
        </div>
    );
}

export default RichTextEditor;
